"use client";

import { useSnapshot } from "valtio";
import { cn } from "utils";

import { inviteCodeState } from "@utils/invite/inviteCodeStore";
import { createInviteCodeCopy } from "./inviteCodeCopy";
import {
  INVITE_CODE_EVENTS,
  type InviteCodeEntryPage,
} from "./inviteCodeEvents";

/**
 * 起始页 / 登录页的邀请码入口行 —— 只有两态，占同一位置（fe-spec D7）：
 *
 * - **有码**（Figma `110013:130291` 登录页 / `110013:130174` 起始页）：`✓ Invite code <code> applied`，code 段加下划线
 * - **无码**（`110013:130019` / `110013:130119`）：`Have an invite code?`
 *
 * 两态都可点击 → 打开填码弹层（有码时预填，可改可清空）。
 * 链接码被后端判无效时已被清掉（`useInviteCodeCapture`），所以这里「有码即已应用」。
 */
interface InviteCodeEntryRowProps {
  /** 点击打开填码弹层 */
  onClick: () => void;
  /** 埋点定位：起始页 / 登录页 */
  page: InviteCodeEntryPage;
  /** 起始页在深色背景上（白字），登录页在浅色背景上（品牌色） */
  tone?: "on_dark" | "on_light";
  /**
   * 尺寸形态：
   * - `inline`（默认）：纯文字行，高 20（登录页 `110013:131112` / `130317`）
   * - `stacked`：起始页 Secondary links 内（`110013:130153` / `131078`）——
   *   无码态是 `Button / General`（py-12 → 高 44），已应用态是纯文字行（高 20）
   */
  variant?: "inline" | "stacked";
  className?: string;
  locale?: string;
}

export function InviteCodeEntryRow({
  onClick,
  page,
  tone = "on_light",
  variant = "inline",
  className,
  locale,
}: InviteCodeEntryRowProps) {
  const { code } = useSnapshot(inviteCodeState);
  const copy = createInviteCodeCopy(locale);

  return (
    <button
      type="button"
      onClick={onClick}
      data-openreplay-label="login_invite_code_entry"
      data-or-event={INVITE_CODE_EVENTS.entryTap}
      data-or-param-page={page}
      data-or-param-state={code ? "applied" : "empty"}
      data-testid={code ? "invite-code-applied-row" : "invite-code-entry-row"}
      className={cn(
        "button_m w-full text-center",
        variant === "stacked" && !code && "rounded-lg px-4 py-3",
        tone === "on_dark"
          ? "text-content_inverse_primary"
          : "text-content_brand_primary",
        className,
      )}
    >
      {code ? (
        <>
          {copy.appliedPrefix}
          <span className="underline">{code}</span>
          {copy.appliedSuffix}
        </>
      ) : (
        copy.haveACode
      )}
    </button>
  );
}
